import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {
  AddSamplesToTableTrackingParams,
  trackAddSamples,
} from 'notebook/sample-propagation/util/addSamplesTracking';
import {NotebookLookupConfigModalQuery} from 'notebook/structured-tables/lookup-columns/__generated/NotebookLookupConfigModalQuery';
import {NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY} from 'notebook/structured-tables/lookup-columns/runLookupConfigModalForNotebook';
import {
  StructuredTableAddOperationOptions,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/TableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';

import AddSamplesFromStructuredTableModal, {
  DestinationSampleFiltersAndColumns,
} from './add-samples-modal/AddSamplesFromStructuredTableModal';
import {dedupeSampleFilters, SampleFilterType} from './SampleFilter';
import {AddToTableOptions} from './util/addSamplesToTable';
import getAcceptableSampleFiltersByColumn from './util/getAcceptableSampleFiltersByColumn';
import makeSampleCells from './util/makeSampleCells';

interface AddSamplesFromStructuredTableParams extends StructuredTableAddOperationOptions {
  destinationTable: UnknownStructuredTable;
  adapter: UnknownStructuredTableAdapter;
  note: Note | null;
  entryId: string;
  addSamples: (
    samplesByColumn: Array<{samples: Array<Cell>; columnIndex: number}>,
    options?: AddToTableOptions
  ) => void;
  destinationColumnIndex?: number;
  trackingParams?: AddSamplesToTableTrackingParams;
}

/**
 * Allows the user to add rows to a table from another table. This function handles the entire
 * operation, from opening the "Add samples" modal through to adding the rows to the table.
 */
export default async function addSamplesFromStructuredTable(
  params: AddSamplesFromStructuredTableParams
): Promise<void> {
  const {destinationTable, adapter, note, entryId, addSamples, destinationColumnIndex, trackingParams} =
    params;
  const convertedTable = adapter.toTable(destinationTable);
  const destinationSampleFiltersByColumn: Array<DestinationSampleFiltersAndColumns> =
    getAcceptableSampleFiltersByColumn(convertedTable).map((sampleTypeByColumn) => ({
      ...sampleTypeByColumn,
      sampleFilter: sampleTypeByColumn.sampleFilters[0] ?? null,
      sampleFilters: sampleTypeByColumn.sampleFilters,
      columns: sampleTypeByColumn.columnIndices.map((index) => destinationTable.columns[index]),
      columnDefinitions: sampleTypeByColumn.columnIndices.map(
        (index) => convertedTable.columnConstraints[index]?.columnDefinition ?? null
      ),
    }));

  const limitSamplesTo = dedupeSampleFilters(
    destinationSampleFiltersByColumn
      .flatMap(({sampleFilters}) => sampleFilters)
      .filter((sampleFilters) => sampleFilters != null)
  );

  // Make sure that all schemas are loaded into cache so that we can infer the final type of lookup
  // columns for column matching. This uses the cache if the schemas have already been loaded.
  await withLoadingScreen(
    doQuerySafe<NotebookLookupConfigModalQuery, {}>({query: NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY})
  );

  const sessionId = analytics.makeAnalyticsEventId();
  const addSamplesCommand = await legacyRunModal(AddSamplesFromStructuredTableModal, {
    sessionId,
    destinationSampleFiltersByColumn,
    note,
    entryId,
    limitSamplesTo,
    destinationTable,
    options: {
      preventTableExpansion: params.preventTableExpansion,
      preventTableExpansionMessage: params.preventTableExpansionMessage,
    },
    columnIndex: destinationColumnIndex,
  });

  if (addSamplesCommand) {
    // The modal only sees a filtered subset of Array<StructuredTableColumn>. We need to convert the
    // destination column(s) back into an index into the master `Array<StructuredTableColumn>` array.
    const {samples, destinationColumnsCids, pasteOption: pasteType} = addSamplesCommand;

    assert(
      destinationColumnsCids != null,
      'Must provide valid destination column ids when adding samples to table'
    );
    const destinationColumnIndices = destinationColumnsCids.map((cid) =>
      destinationTable.columns.findIndex((column) => column.cid === cid)
    );
    assert(!destinationColumnIndices.includes(-1), 'Sample destination column cid not found', {
      destinationColumnsCids,
    });

    const destinationInfo = destinationSampleFiltersByColumn.find(({columnIndices}) =>
      isEqual(columnIndices, destinationColumnIndices)
    );
    assert(
      destinationInfo != null,
      'Invalid destination column indices, cannot find corresponding destinationInfo'
    );
    const {sampleFilter} = destinationInfo;

    assert(
      destinationColumnIndices.length === 1 ||
        (sampleFilter.type === SampleFilterType.WELL && destinationColumnIndices.length === 2),
      'Multiple destination columns only supported for wells, which must have exactly 2 destination columns'
    );

    // The cells we create need to match the column type they're being inserted into
    const columnDefinitions = destinationColumnIndices.map(
      (index) => convertedTable.columnConstraints[index]!.columnDefinition!
    );

    const newCells = makeSampleCells(samples, sampleFilter.type, columnDefinitions);

    if (sampleFilter.type === SampleFilterType.WELL) {
      assert(
        newCells.length === 2,
        `Must provide exactly 2 columns of cells to insert as wells, got ${newCells.length} column(s) instead`
      );
      addSamples(
        [
          {samples: newCells[0].cells, columnIndex: destinationColumnIndices[0]},
          {samples: newCells[1].cells, columnIndex: destinationColumnIndices[1]},
        ],
        {pasteOption: pasteType}
      );
    } else {
      assert(
        newCells.length === 1,
        `Must provide exactly 1 column of cells to insert as non-wells, got ${newCells.length} column(s) instead`
      );
      addSamples([{samples: newCells[0].cells, columnIndex: destinationColumnIndices[0]}], {
        pasteOption: pasteType,
      });
    }
    if (trackingParams) {
      trackAddSamples({
        ...trackingParams,
        numSamples: newCells[0].cells.length,
        sourceId: null,
        sourceType: 'structured_table',
        sessionId,
      });
    }
  }
}
